import { AppContext } from "Context/AppContext";
import { getOtherRevenue } from "Helpers/otherRevenue";
import { setAllStore } from "Helpers/setAllStore";
import moment from "moment";
import momentTimezone from 'moment-timezone';
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardData } from "../../../api/report";
import averageCommission from "../../../Assets/images/icons/home.png";
import iconImg from "../../../Assets/images/icons/how_to_vote-24px 1.png";
import orderImg from "../../../Assets/images/icons/local_offer-24px 1.png";
import orderTaken from "../../../Assets/images/icons/Vector (1).png";
import userReg from "../../../Assets/images/icons/Vector (2).png";
import commissionEarned from "../../../Assets/images/icons/Vector (3).png";
import totalRevenue from "../../../Assets/images/icons/Vector.png";
import Commission from "../charts/Commission";
import CustomerGrowth from "../charts/CustomerGrowth";
import PieChart from "../charts/PieChart";
import Productivity from "../charts/Productivity";
import RevenueBreakdown from "../charts/RevenueBreakdown";
import Header from "./../../../Components/Header/Header";
import Listbox from "./Listbox";
import PopularProduct from "./PopularProduct";
import { DEFAULT_TIMEZONE } from "Helpers/Constants";
import GlobalDateFilters from "Components/GlobalDateFilters/GlobalDateFilters";

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = React.useState([]);
  const [otherRevenue, setOtherRevenue] = useState(null);
  const today = momentTimezone.tz(localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE).format("YYYY-MM-DD");
  const [type, setType] = useState("delivery_date");

  const { showToastMessage, setIsLoading } = useContext(AppContext);
  const [timeValue, setTimeValue] = useState("today");
  const [startDateDisplay, setStartDateDisplay] = useState(today);
  const [endDateDisplay, setEndDateDisplay] = useState(today);

  const [checkAllStore,setCheckAllStore] = useState(true);

  const customDateValidation = (startDate, endDate) => {
    const diff = moment(endDate).diff(moment(startDate), "days");
    const fromDate = moment(endDate).add(1, "day").isAfter(moment(startDate))
    const toDate = moment(startDate).subtract(1, "day").isBefore(moment(endDate));
    if(diff > 31){
      showToastMessage(undefined, "You can't select custom date range more than 31 days.", "error", false);
      return false;
    }else if(!fromDate || !toDate){
      showToastMessage(undefined, "End date should be greater than start date.", "error", false);
      return fromDate && toDate;
    }else return true;
  }

  const fetchData = async () => {
    const isCustomDateValid = customDateValidation(startDateDisplay, endDateDisplay);
    if(!isCustomDateValid) return;
    setIsLoading(true);

    setAllStore(checkAllStore);

    let range = "monthly";
    if (timeValue === "PastWeek") {
      range = "weekly";
    }
    const postData = {
      start_date: startDateDisplay,
      end_date: endDateDisplay,
      date_range_type: range,
      date_type: type,
    };

    const response = await getDashboardData(postData);
    if (response && response.status === 200) {
      setData(response.data.data);
    } else if (response?.status === 403) {
      navigate("/profile");
    } else {
      showToastMessage(undefined, response?.data?.message, "error", false);
    }
    setIsLoading(false);

    // Fetched here, not off the checkbox: toggling All Stores does not refetch the
    // dashboard, so a tile that moved on the toggle would sit beside stale totals.
    setOtherRevenue(checkAllStore
      ? null
      : await getOtherRevenue(localStorage.getItem("selectedStore")));
    
    // Clear the all-store flag so later API calls use the dropdown store.
    if(checkAllStore){ setAllStore(false) }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const ordersTiles = [
    { number: data.ordersTaken || 0, icon: orderTaken, title: "Orders Taken" },
    { number: data.outForDelivery || 0, icon: iconImg, title: "Orders For Delivery" },
    { number: data.avgOrderValue ? `$${data.avgOrderValue}` : 0, icon: orderImg, title: "Average Order Value" },
    { number: `$${data.walkinAvgOrderValue || "0.00"}`, icon: orderImg, title: "Walkin AOV" },
    { number: `$${data.csrAvgOrderValue || "0.00"}`, icon: orderImg, title: "CSR AOV" },
  ];

  const salesTiles = [
    { number: data.totalRevenue ? `$${data.totalRevenue}` : 0, icon: totalRevenue, title: "Total Revenue" },
    ...(otherRevenue
      ? [
          {
            // typeof, not truthiness: 0 is a real figure and a non-number would throw.
            number: typeof otherRevenue.other_revenue === "number"
              ? `$${otherRevenue.other_revenue.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : "Not closed",
            icon: totalRevenue,
            title: `Other Revenue (${otherRevenue.month})`,
          },
        ]
      : []),
    { number: data.newUserRegistration || 0, icon: userReg, title: "User Registration" },
  ];

  const commissionTiles = [
    { number: `$${data.totalCommission || 0}`, icon: commissionEarned, title: "Commission Earned" },
    { number: `$${data.avgCommissionByUser || 0}`, icon: totalRevenue, title: "Average Commission per Agent" },
    { number: `$${data.avgCommissionByItems || 0}`, icon: averageCommission, title: "Commission by Item" },
  ];

  const CommissionData = {
    label: data.commissionData?.commissionDataKey ?? 0,
    data: data.commissionData?.commissionDataValue ?? 0,
  };
  const ProductivityData = {
    label: data.productivityReportData?.productivityReportDataKey ?? 0,
    data: data.productivityReportData?.productivityReportDataValue ?? 0,
  };
  const CustomerGrowthData = {
    label: data.customerGrowthReportData?.customerGrowthReportDataKey ?? 0,
    oldData: data.customerGrowthReportData?.customerGrowthReportDataValueOld ?? 0,
    newData: data.customerGrowthReportData?.customerGrowthReportDataValueNew ?? 0,
  };

  const rb = data.revenueBreakDownData;
  const RevenueData = {
    label: rb ? [...rb.revenueBreakDownDataKey, ...data.revenueBreakDownPayment.map((d) => d.title)] : [],
    data: rb ? [...rb.revenueBreakDownDataValue, ...data.revenueBreakDownPayment.map((d) => d.status)] : [],
  };
  const revenueBreakDownData = {
    revenueBreakDownOrderData: data.revenueBreakDownOrderData || [],
    revenueBreakDownOrderCount: data.revenueBreakDownOrderCount || [],
  };

  const selectedPeriodOptions = [
    { id: "today", title: "Today" },
    { id: "yesterday", title: "Yesterday" },
    { id: "weekly", title: "Weekly" },
    { id: "monthly", title: "Monthly" },
    { id: "custom", title: "Custom" },
  ]

  return (
    <React.Fragment>
      <Header title="Overview" />
      <div id="MainContent" className="main-content">
        <div className="row">
          <div className="col-12 p-0">
            <GlobalDateFilters
              isAllCheckStoreBnt
              isDateFilter
              isFilterByOption
              isFilterByPeriod
              isSearchBtn
              isDisabled={timeValue === "today" || timeValue === "yesterday"}
              fromDate={startDateDisplay}
              toDate={endDateDisplay}
              setFromDate={setStartDateDisplay}
              setToDate={setEndDateDisplay}
              type={type}
              setType={setType}
              selectedPeriod={timeValue}
              setSelectedPeriod={setTimeValue}
              periodOptions={selectedPeriodOptions}
              fetchData={fetchData}
              defaultSetCheckAllStore={checkAllStore}
              checkAllStore={setCheckAllStore}
              key={"dashboard"}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-lg-3 mt-3 pl-3" style={{ paddingLeft: "0px" }}>
            <a href="/admin/orders/process">
              <Listbox list={ordersTiles} />
            </a>
          </div>
          <div className="col-12 col-lg-9 mt-3">
            <PopularProduct list={data.mostPopularProductData} />
          </div>
        </div>
        <br></br>
        <div className={`row`}>
          <div className={`col-12 col-lg-9`}>
            <CustomerGrowth customerGrowthData={CustomerGrowthData} />
          </div>
          <div className={`col-12 col-lg-3 `}>
            <br></br>
            <span>
              <Listbox list={salesTiles} />
            </span>
          </div>
        </div>
        <br></br>
        <div className={`row`}>
          <div className={`col-12 col-lg-11`}>
            <Productivity productivityData={ProductivityData} />
          </div>
        </div>
        <br></br>
        <div className={`row`}>
          <div className={`col-12  col-lg-9`}>
            <Commission commissionData={CommissionData} />
          </div>
          <div className={`col-12 col-lg-3`}>
            <br></br>
            <a href="/reports/commission">
              <Listbox list={commissionTiles} />
            </a>
          </div>
        </div>
        <br></br>
        <div className={` row `}>
          <div className={`col-12 col-lg-9`}>
            <RevenueBreakdown revenueData={RevenueData} />
          </div>
          <div style={{ marginTop: "20px" }} className={`col-12 col-lg-3 `}>
            <PieChart chartData={revenueBreakDownData} />
          </div>
        </div>
        <br></br>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
